import Phaser from 'phaser';
import { GameScene } from '../GameScene';

/**
 * Pantalla completa + reset de input por pérdida de foco. Maneja los dos triggers de fullscreen
 * (botón HUD vía Fullscreen API y F11 nativo del browser, convergiendo en syncFullscreenDisplay
 * idempotente) y limpia las teclas de movimiento fantasma al perder foco (alt-tab / cambio de
 * pestaña) para que no dispare auto-walk. Extraído de GameScene (god-object). El estado de wireado
 * vive acá; el resto (scale, fullscreenButton, minimap, playerObjects…) se accede vía `this.scene.*`.
 */
export class FullscreenController {
  constructor(private scene: GameScene) {}

  /** Wireado del sistema de pantalla completa. Cubre dos triggers distintos:
   * - Botón `⛶` del HUD → Fullscreen API (this.scene.scale.toggleFullscreen()), que emite los
   *   eventos ENTER/LEAVE_FULLSCREEN de Phaser.
   * - Tecla F11 → fullscreen nativo del browser, que NO usa la Fullscreen API (no setea
   *   document.fullscreenElement ni emite eventos de Phaser) pero sí iguala el viewport a la
   *   pantalla → lo detectamos por el evento `resize` de window.
   * En ambos casos convergemos en syncFullscreenDisplay(), que es idempotente. */
  public fullscreenWired = false;
  public setupFullscreen(): void {
    if (this.fullscreenWired) return;
    this.fullscreenWired = true;
    const onResize = (): void => this.syncFullscreenDisplay();
    this.scene.scale.on(Phaser.Scale.Events.ENTER_FULLSCREEN, this.syncFullscreenDisplay, this);
    this.scene.scale.on(Phaser.Scale.Events.LEAVE_FULLSCREEN, this.syncFullscreenDisplay, this);
    window.addEventListener('resize', onResize);
    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scene.scale.off(Phaser.Scale.Events.ENTER_FULLSCREEN, this.syncFullscreenDisplay, this);
      this.scene.scale.off(Phaser.Scale.Events.LEAVE_FULLSCREEN, this.syncFullscreenDisplay, this);
      window.removeEventListener('resize', onResize);
      this.fullscreenWired = false;
    });
  }

  /** ¿El browser está en fullscreen nativo (F11)? A diferencia de la Fullscreen API, F11 no
   * setea document.fullscreenElement ni dispara eventos de Phaser, pero sí hace que el viewport
   * ocupe TODA la pantalla (sin barra de tareas ni chrome del browser). Requerimos que coincidan
   * ancho Y alto: una ventana MAXIMIZADA tiene innerWidth≈screen.width pero innerHeight bastante
   * menor (barra de título + chrome + barra de tareas), así que el alto descarta ese falso
   * positivo. Tolerancia holgada para redondeos/DPI. */
  public isBrowserFullscreen(): boolean {
    return (
      window.innerWidth >= window.screen.width - 4 &&
      window.innerHeight >= window.screen.height - 4
    );
  }

  /** Alterna la pantalla completa del browser vía la Fullscreen API. Debe llamarse desde el
   * handler de un gesto del usuario (pointerup del botón), requisito del navegador. Los eventos
   * ENTER/LEAVE_FULLSCREEN de Phaser disparan syncFullscreenDisplay(). */
  public toggleFullscreen(): void {
    this.scene.scale.toggleFullscreen();
  }

  /** Fuente de verdad del display en pantalla completa. El modo de escala es SIEMPRE FIT
   * (fijado en la config); acá sólo cambiamos el TAMAÑO del contenedor: en fullscreen toma la
   * clase `.fullscreen` (crece al viewport → FIT estira el canvas), fuera vuelve a 1280×960 fijo
   * (FIT queda en factor 1.0 = pixel-perfect). Nunca conmutamos scaleMode (eso dejaba estilos a
   * medias y bugueaba al salir). Idempotente: seguro de llamar en cada evento/resize. */
  public syncFullscreenDisplay(): void {
    const shouldStretch = this.scene.scale.isFullscreen || this.isBrowserFullscreen();
    const container = document.getElementById('game-container');
    const link = document.getElementById('aureum-link');
    if (container) container.classList.toggle('fullscreen', shouldStretch);
    if (link) link.style.display = shouldStretch ? 'none' : '';
    // Recalcular el fit contra el nuevo tamaño del contenedor (Phaser lee los bounds del parent).
    this.scene.scale.refresh();
    // Belt-and-suspenders para F11: al salir, el resize a veces dispara ANTES de que el layout
    // (tamaño real del contenedor) se haya asentado; un segundo refresh en el próximo frame agarra
    // las dimensiones definitivas y evita que el canvas quede mal escalado/centrado.
    requestAnimationFrame(() => this.scene.scale.refresh());
    // Reflejar el estado en el botón del HUD.
    this.refreshFullscreenButton();
  }

  /** Actualiza el glyph del botón de pantalla completa según el estado actual. */
  public refreshFullscreenButton(): void {
    if (!this.scene.fullscreenButton) return;
    const active = this.scene.scale.isFullscreen || this.isBrowserFullscreen();
    // ⛶ = entrar a pantalla completa · ⤢ (flechas hacia adentro) = salir.
    this.scene.fullscreenButton.setText(active ? '⤢' : '⛶');
  }

  /** Wireado una sola vez: resetea el estado de teclas cuando el juego pierde
   * foco, para que ninguna tecla de movimiento quede "apretada" y dispare
   * auto-walk. Cubre alt-tab, cambio de pestaña y pérdida de foco del canvas. */
  public focusLossResetWired = false;
  public setupFocusLossInputReset(): void {
    if (this.focusLossResetWired) return;
    this.focusLossResetWired = true;
    const onBlur = (): void => this.resetMovementInput();
    const onVisibility = (): void => {
      if (document.hidden) this.resetMovementInput();
    };
    // Evento nativo de Phaser al perder foco la ventana del juego.
    this.scene.game.events.on(Phaser.Core.Events.BLUR, onBlur);
    // Belt-and-suspenders para embebidos donde el BLUR de Phaser no dispara.
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibility);
    // Limpieza al destruir/parar la escena para no dejar listeners colgando
    // apuntando a una instancia vieja si la escena se recrea.
    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scene.game.events.off(Phaser.Core.Events.BLUR, onBlur);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibility);
      this.focusLossResetWired = false;
      this.scene.minimap?.destroy();
      this.scene.minimap = null;
    });
  }

  /** Limpia todo el estado de input de movimiento: borra los isDown fantasma de
   * Phaser y corta la intención de paso pendiente del jugador local. */
  public resetMovementInput(): void {
    this.scene.input.keyboard?.resetKeys();
    if (this.scene.localSessionId) {
      const render = this.scene.playerObjects.get(this.scene.localSessionId);
      if (render) render.pendingDirection = null;
    }
  }
}
